import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { COMPETITION_TYPES, PAYMENT_TIMING_LABELS } from '@concours/shared'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  InfoGrid,
  InfoItem,
  PageHeader,
  Select,
  useAuth,
  useCan,
  useToast,
} from '@concours/ui'
import { FORMS_KEY, type FormDetail, type FormSummary } from '../../lib/form-types'
import { errorMessage, fieldErrors } from '../../lib/errors'
import {
  emptyForm,
  money,
  payloadFrom,
  type CompetitionDetail,
  type CompetitionForm,
} from '../../lib/competition-types'
import { CompetitionFields, FIELD_SECTION, type FieldSection } from './CompetitionFields'
import { COMPETITIONS_KEY } from './CompetitionsPage'

type Step = FieldSection | 'form' | 'stages' | 'summary'
const STEPS: Step[] = ['info', 'conditions', 'calendar', 'fees', 'form', 'stages', 'summary']

/** Champs obligatoires par étape (contrôle immédiat ; l'API revalide tout). */
const REQUIRED: Partial<Record<Step, (keyof CompetitionForm)[]>> = {
  info: ['title', 'reference', 'session', 'year', 'corps', 'seats', 'applicationPrefix'],
}

/**
 * Assistant de création (TODO phase 6) : informations → conditions → calendrier
 * → frais → formulaire → étapes → récapitulatif. Crée un BROUILLON : rien n'est
 * visible du public avant la publication.
 */
export function CompetitionWizardPage() {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>('info')
  const [form, setForm] = useState<CompetitionForm>(emptyForm)
  const [missing, setMissing] = useState<Record<string, string>>({})
  const [formId, setFormId] = useState('')
  const [configureStages, setConfigureStages] = useState(true)
  const canListForms = useCan('forms.view')
  const forms = useQuery({
    queryKey: FORMS_KEY,
    queryFn: () => session.api.get<{ data: FormSummary[] }>('/admin/forms').then((r) => r.data),
    enabled: canListForms,
  })
  const publishedForms = (forms.data ?? []).filter((f) => f.latestVersion !== null)
  const chosenForm = useQuery({
    queryKey: [...FORMS_KEY, formId],
    queryFn: () => session.api.get<FormDetail>(`/admin/forms/${formId}`),
    enabled: Boolean(formId) && canListForms,
  })
  // Dernière version publiée du formulaire choisi (associée juste après la création).
  const formVersion = (chosenForm.data?.versions ?? [])
    .filter((v) => v.status === 'published')
    .sort((a, b) => Number(b.version) - Number(a.version))[0]

  const create = useMutation({
    mutationFn: async () => {
      const created = await session.api.post<CompetitionDetail>(
        '/admin/competitions',
        payloadFrom(form)
      )
      let formLinked = true
      if (formVersion)
        await session.api
          .put(`/admin/competitions/${created.id}/form`, {
            formVersionId: formVersion.id,
            reason: null,
          })
          .catch(() => {
            formLinked = false
          })
      return { created, formLinked }
    },
    onSuccess: async ({ created, formLinked }) => {
      await queryClient.invalidateQueries({ queryKey: COMPETITIONS_KEY })
      toast.success(t('competitions.wizard.created'), created.reference)
      if (!formLinked) toast.error(t('competitions.wizard.formLinkFailed'))
      navigate(configureStages ? `/selection/${created.id}` : `/concours/${created.id}`)
    },
    onError: (error) => {
      const fields = fieldErrors(error)
      const first = Object.keys(fields)[0]
      if (first && FIELD_SECTION[first]) setStep(FIELD_SECTION[first])
      else toast.error(errorMessage(error))
    },
  })
  const errors = { ...missing, ...fieldErrors(create.error) }
  const stepsWithErrors = new Set(
    Object.keys(errors)
      .map((f) => FIELD_SECTION[f])
      .filter(Boolean)
  )

  const index = STEPS.indexOf(step)
  const go = (next: Step) => {
    const required = REQUIRED[step] ?? []
    const empty = required.filter((f) => !String(form[f]).trim())
    if (STEPS.indexOf(next) > index && empty.length) {
      setMissing(Object.fromEntries(empty.map((f) => [f, t('competitions.wizard.required')])))
      return
    }
    setMissing({})
    setStep(next)
  }
  const patch = (p: Partial<CompetitionForm>) => setForm((f) => ({ ...f, ...p }))

  return (
    <>
      <Link
        to="/concours"
        className="btn btn-ghost btn-sm"
        style={{ marginBlockEnd: 'var(--space-3)' }}
      >
        <ArrowLeft aria-hidden="true" /> {t('competitions.back')}
      </Link>
      <PageHeader
        eyebrow={t('competitions.eyebrow')}
        title={t('competitions.wizard.title')}
        description={t('competitions.wizard.description')}
      />

      <nav aria-label={t('competitions.wizard.steps')}>
        <ol className="wizard-steps">
          {STEPS.map((s) => (
            <li key={s}>
              <button
                type="button"
                aria-current={s === step ? 'step' : undefined}
                data-error={stepsWithErrors.has(s as FieldSection) || undefined}
                onClick={() => go(s)}
              >
                {t(`competitions.wizard.step.${s}`)}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <Card>
        <CardHeader title={t(`competitions.wizard.step.${step}`)} />
        <CardBody>
          {(step === 'info' || step === 'conditions' || step === 'calendar' || step === 'fees') && (
            <CompetitionFields section={step} form={form} onChange={patch} errors={errors} />
          )}
          {step === 'form' && (
            <div className="stack" style={{ gap: 'var(--space-3)' }}>
              <p style={{ margin: 0 }}>{t('competitions.wizard.formHint')}</p>
              {canListForms ? (
                <Select
                  label={t('competitions.wizard.formField')}
                  value={formId}
                  placeholder={t('competitions.wizard.formNone')}
                  options={publishedForms.map((f) => ({
                    value: f.id,
                    label: `${f.title} (${f.code}) — v${f.latestVersion}`,
                  }))}
                  hint={
                    publishedForms.length
                      ? t('competitions.wizard.formFieldHint')
                      : t('competitions.wizard.formEmpty')
                  }
                  onChange={(e) => setFormId(e.target.value)}
                />
              ) : (
                <Alert tone="info">{t('competitions.wizard.formNoPermission')}</Alert>
              )}
              <p className="text-muted" style={{ margin: 0 }}>
                <Link to="/formulaires">{t('competitions.wizard.formManage')}</Link>
              </p>
            </div>
          )}
          {step === 'stages' && (
            <div className="stack" style={{ gap: 'var(--space-3)' }}>
              <p style={{ margin: 0 }}>{t('competitions.wizard.stagesHint')}</p>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={configureStages}
                  onChange={(e) => setConfigureStages(e.target.checked)}
                />
                <span>{t('competitions.wizard.stagesAfter')}</span>
              </label>
            </div>
          )}
          {step === 'summary' && (
            <Summary
              form={form}
              formLabel={
                formVersion && chosenForm.data
                  ? `${chosenForm.data.title} — v${formVersion.version}`
                  : null
              }
            />
          )}
        </CardBody>
      </Card>

      <div
        className="row"
        style={{ justifyContent: 'space-between', marginBlockStart: 'var(--space-4)' }}
      >
        <Button
          variant="secondary"
          icon={<ArrowLeft />}
          disabled={index === 0}
          onClick={() => go(STEPS[index - 1]!)}
        >
          {t('competitions.wizard.previous')}
        </Button>
        {step === 'summary' ? (
          <Button
            variant="primary"
            icon={<Check />}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            {t('competitions.wizard.create')}
          </Button>
        ) : (
          <Button variant="primary" iconEnd={<ArrowRight />} onClick={() => go(STEPS[index + 1]!)}>
            {t('competitions.wizard.next')}
          </Button>
        )}
      </div>
    </>
  )
}

function Summary({ form, formLabel }: { form: CompetitionForm; formLabel: string | null }) {
  const { t } = useTranslation()
  const date = (v: string) =>
    v ? new Date(v).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—'
  return (
    <div className="stack">
      <Alert tone="info">{t('competitions.wizard.summaryHint')}</Alert>
      <InfoGrid>
        <InfoItem label={t('competitions.fields.title')}>{form.title || '—'}</InfoItem>
        <InfoItem label={t('competitions.fields.reference')}>
          <span className="mono">{form.reference || '—'}</span>
        </InfoItem>
        <InfoItem label={t('competitions.fields.type')}>{COMPETITION_TYPES[form.type]}</InfoItem>
        <InfoItem label={t('competitions.fields.session')}>{form.session}</InfoItem>
        <InfoItem label={t('competitions.fields.corps')}>{form.corps || '—'}</InfoItem>
        <InfoItem label={t('competitions.fields.seats')}>{form.seats || '—'}</InfoItem>
        <InfoItem label={t('competitions.fields.prefix')}>
          <span className="mono">{`${form.applicationPrefix || '???'}-${form.year}-XXXXXX`}</span>
        </InfoItem>
        <InfoItem label={t('competitions.fields.opensAt')}>{date(form.opensAt)}</InfoItem>
        <InfoItem label={t('competitions.fields.closesAt')}>{date(form.closesAt)}</InfoItem>
        <InfoItem label={t('competitions.fields.paymentTiming')}>
          {PAYMENT_TIMING_LABELS[form.paymentTiming]}
        </InfoItem>
        <InfoItem label={t('competitions.fields.feeAmount')}>
          {form.paymentTiming === 'not_required' ? '—' : money(Number(form.feeAmount || 0))}
        </InfoItem>
        <InfoItem label={t('competitions.fields.requiredDocuments')}>
          {form.requiredDocuments.filter(Boolean).length}
        </InfoItem>
        <InfoItem label={t('competitions.wizard.step.form')}>
          {formLabel ?? t('competitions.wizard.formNone')}
        </InfoItem>
      </InfoGrid>
    </div>
  )
}
